package er.prateek.crossly.ui.theme

import androidx.compose.runtime.Immutable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import er.prateek.crossly.data.model.DifficultyTier

/**
 * The game's semantic color tokens — everything Material's [androidx.compose.material3.ColorScheme]
 * has no slot for: the animated backdrop, sticker-button shadows, reward gold, streak flame,
 * per-tier identity colors, and the eleven distinct crossword cell states.
 *
 * Read it through [LocalGameColors] (or `MaterialTheme.game`); each palette in
 * [AppColorPalettes] supplies one light and one dark instance.
 */
@Immutable
class GameColors(
    // --- Backdrop: the gradient canvas every screen sits on, plus its two drifting glow blobs.
    val backdropTop: Color,
    val backdropBottom: Color,
    val glowA: Color,
    val glowB: Color,
    val gridTexture: Color,

    // --- Surfaces
    val card: Color,
    val cardElevated: Color,
    val cardBorder: Color,
    /** Drawn under sticker buttons/cards to fake extruded depth — no real elevation needed. */
    val shadow: Color,

    // --- Brand accents
    val accent: Color,
    val accentAlt: Color,
    val onAccent: Color,
    val accentSoft: Color,

    // --- Ink
    val ink: Color,
    val inkMuted: Color,

    // --- Reward / status
    val gold: Color,
    val goldEmpty: Color,
    val flame: Color,
    val success: Color,
    val danger: Color,
    val locked: Color,

    // --- Crossword grid
    val cellFill: Color,
    val cellBorder: Color,
    val cellText: Color,
    val cellNumber: Color,
    val cellBlock: Color,
    /** The whole word the cursor sits in. */
    val cellWord: Color,
    /** The single active cell. */
    val cellFocus: Color,
    val cellCorrect: Color,
    val cellIncorrect: Color,
    val cellRevealed: Color,
    /** Starter letters the puzzle opens with — printed, not played. */
    val cellGiven: Color,

    // --- Difficulty tiers
    val tierEasy: Color,
    val tierMedium: Color,
    val tierHard: Color,
    val tierExpert: Color,
) {
    fun tierColor(tier: DifficultyTier): Color = when (tier) {
        DifficultyTier.EASY -> tierEasy
        DifficultyTier.MEDIUM -> tierMedium
        DifficultyTier.HARD -> tierHard
        DifficultyTier.EXPERT -> tierExpert
    }

    val accentBrush: Brush get() = Brush.linearGradient(listOf(accent, accentAlt))
    val goldBrush: Brush get() = Brush.linearGradient(listOf(gold, flame))
    fun tierBrush(tier: DifficultyTier): Brush {
        val base = tierColor(tier)
        return Brush.linearGradient(listOf(base, base.mixWith(accentAlt, 0.35f)))
    }
}

/** Linear blend toward [other]; used to derive hover/press/gradient stops from a base token. */
fun Color.mixWith(other: Color, amount: Float): Color = Color(
    red = red + (other.red - red) * amount,
    green = green + (other.green - green) * amount,
    blue = blue + (other.blue - blue) * amount,
    alpha = alpha + (other.alpha - alpha) * amount,
)

val LocalGameColors = staticCompositionLocalOf<GameColors> {
    error("No GameColors provided — wrap the UI in AppTheme { }")
}
